// Per-step browser telemetry: q_shown, first_keystroke, typing_batch (every 5 s), paste,
// focus_lost / focus_back, q_submitted. Only counts and timings, never what was typed or pasted.
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { ClipboardEvent, KeyboardEvent } from "react";

import { Event } from "../../telemetry/events";
import type { Telemetry } from "../../telemetry/posthog";
import { TypingTracker } from "../../telemetry/typing";

const BATCH_MS = 5000;

export function useStepTelemetry(telemetry: Telemetry, question: string | null) {
  const shownAt = useRef(0); // set when the step is shown (effect below)
  const firstKey = useRef(false);
  const tracker = useMemo(() => new TypingTracker(), []);

  const flushTyping = useCallback(() => {
    const batch = question ? tracker.flush() : null;
    if (batch && question) telemetry.capture(Event.TYPING_BATCH, { question, ...batch });
  }, [telemetry, tracker, question]);

  useEffect(() => {
    if (!question) return;
    shownAt.current = performance.now();
    firstKey.current = false;
    telemetry.capture(Event.Q_SHOWN, { question });

    let awaySince: number | null = null;
    const lost = () => {
      if (awaySince !== null) return;
      awaySince = performance.now();
      telemetry.capture(Event.FOCUS_LOST, { question });
    };
    const back = () => {
      if (awaySince === null) return;
      const away_ms = Math.round(performance.now() - awaySince);
      awaySince = null;
      telemetry.capture(Event.FOCUS_BACK, { question, away_ms });
    };
    const visibility = () => (document.hidden ? lost() : back());
    window.addEventListener("blur", lost);
    window.addEventListener("focus", back);
    document.addEventListener("visibilitychange", visibility);
    const timer = window.setInterval(flushTyping, BATCH_MS);
    return () => {
      window.removeEventListener("blur", lost);
      window.removeEventListener("focus", back);
      document.removeEventListener("visibilitychange", visibility);
      window.clearInterval(timer);
    };
  }, [telemetry, question, flushTyping]);

  const msSinceShown = () => Math.round(performance.now() - shownAt.current);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const special = event.key.length > 1 && !["Backspace", "Delete", "Enter"].includes(event.key);
      if (!question || special) return; // ignore arrows, shift, etc.
      if (!firstKey.current) {
        firstKey.current = true;
        telemetry.capture(Event.FIRST_KEYSTROKE, { question, ms_since_shown: msSinceShown() });
      }
      tracker.key(performance.now(), event.key === "Backspace" || event.key === "Delete");
    },
    [telemetry, tracker, question],
  );

  const onPaste = useCallback(
    (event: ClipboardEvent<HTMLElement>) => {
      if (!question) return;
      const length = event.clipboardData.getData("text").length;
      telemetry.capture(Event.PASTE, { question, length, ms_since_shown: msSinceShown() });
    },
    [telemetry, question],
  );

  const submitted = useCallback(
    (chars: number, autoSubmitted: boolean) => {
      if (!question) return;
      flushTyping();
      telemetry.capture(Event.Q_SUBMITTED, {
        question,
        chars,
        ms_total: msSinceShown(),
        auto_submitted: autoSubmitted,
      });
    },
    [telemetry, question, flushTyping],
  );

  return { onKeyDown, onPaste, submitted };
}
